import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { Flex, Pagination, Row, Spin } from '@duxton-web/ui';
import { useDispatch, useSelector } from 'react-redux';
import { AddUniversal, CopyOutlined } from '@duxton-web/icons';

import Filter from 'omega-rtc/components/form/ucd/Filter';
import ProjectCard from 'omega-rtc/components/form/ucd/ProjectCard';
import {
  PROJECT_COUNTRY_QUERY_KEY,
  PROJECT_MORE_FILTERS_QUERY_KEY,
  PROJECT_OWNERSHIP_QUERY_KEY,
  PROJECT_STATUS_QUERY_KEY,
  PROJECT_SEARCH_QUERY_KEY,
  PROJECT_PAGE_QUERY_KEY,
  PROJECT_PAGE_SIZE_QUERY_KEY,
  PROJECT_DEFAULT_PAGE_SIZE,
  FILTER_FIELDS,
  UCD_ROUTES_LINK,
  UCD_RESOURCE_NAMES,
  UCD_ACTION_TYPES,
} from 'omega-rtc/constants/ucd';
import {
  getCountryOptions,
  generateSearchProjectRequestBody,
  getObjectValue,
  getPositiveNumber,
  updateSearchView,
} from 'omega-rtc/utils/ucd-utils';
import AddCampaignForm from 'omega-rtc/components/form/ucd/AddCampaignForm';
import { createUCDCampaign, searchUCDProjects } from 'omega-rtc/actions/ucd';
import NoContentPlaceHolder from 'components/form/ucd/common/NoContentPlaceHolder';
import NoProjectsPlaceholderIcon from 'img/ucd_no_projects_placeholder.svg';
import './ProjectSearchView.scss';

const ProjectSearchView = (props) => {
  const dispatch = useDispatch();

  const projects = useSelector((state) => state.ucd.projects);
  const projectSearchHits = useSelector((state) => state.ucd.projectSearchHits);
  const countries = useSelector((state) => state.countries.entities);
  const configs = useSelector((state) => state.ucd.configs);
  const user = useSelector((state) => state.app.user);
  const searchProjectRequest = useSelector((state) =>
    _.get(state.requests, 'searchUcdProjectsRequest', {}),
  );

  const [addCampaignModalMeta, setAddCampaignModalMeta] = useState({
    showModal: false,
    project: undefined,
  });
  const shouldFetchEntities = () => {
    const v = _.pickBy(props.searchParams, (value, key) =>
      _.startsWith(key, 'project_'),
    );
    return new URLSearchParams(v).toString();
  };

  useEffect(() => {
    dispatch(
      searchUCDProjects(
        generateSearchProjectRequestBody(props.searchParams, user.email),
      ),
    );
  }, [shouldFetchEntities()]);

  const onMoreFilterChange = (newFilters) =>
    updateSearchView(
      props.searchParams,
      PROJECT_MORE_FILTERS_QUERY_KEY,
      newFilters,
      props.router,
    );

  const onCountryChange = (newValues) =>
    updateSearchView(
      props.searchParams,
      PROJECT_COUNTRY_QUERY_KEY,
      newValues,
      props.router,
    );

  const onPageChange = (page) =>
    updateSearchView(
      props.searchParams,
      PROJECT_PAGE_QUERY_KEY,
      page,
      props.router,
    );

  const onSearchTextChange = (newValue) =>
    updateSearchView(
      props.searchParams,
      PROJECT_SEARCH_QUERY_KEY,
      newValue,
      props.router,
    );

  const onMyProjectChange = (newValue) =>
    updateSearchView(
      props.searchParams,
      PROJECT_OWNERSHIP_QUERY_KEY,
      newValue,
      props.router,
    );

  const onStatusChange = (newValue) =>
    updateSearchView(
      props.searchParams,
      PROJECT_STATUS_QUERY_KEY,
      newValue,
      props.router,
    );

  const addCampaignCallback = (project) => {
    setAddCampaignModalMeta({ showModal: true, project });
  };
  const duplicateProjectCallback = (project) => {
    if (project && project.projectID) {
      props.router.push(UCD_ROUTES_LINK.PROJECT_DUPLICATE(project.projectID));
    }
  };
  const actions = {
    options: [
      {
        label: 'Duplicate',
        key: 'duplicate',
        icon: <CopyOutlined />,
        callback: duplicateProjectCallback,
        condition: {
          resource: UCD_RESOURCE_NAMES.project,
          action: UCD_ACTION_TYPES.create,
        },
      },
      {
        label: 'Add Campaign',
        key: 'add_campaign',
        icon: <AddUniversal />,
        callback: addCampaignCallback,
        condition: {
          resource: UCD_RESOURCE_NAMES.campaign,
          action: UCD_ACTION_TYPES.create,
        },
      },
    ],
  };

  const page = getPositiveNumber(
    _.get(props.searchParams, PROJECT_PAGE_QUERY_KEY),
    1,
  );
  const pageSize = getPositiveNumber(
    _.get(props.searchParams, PROJECT_PAGE_SIZE_QUERY_KEY),
    PROJECT_DEFAULT_PAGE_SIZE,
  );

  return (
    <div>
      <Filter
        type="projects"
        router={props.router}
        countries={getCountryOptions(countries)}
        moreFiltersCallback={onMoreFilterChange}
        countryChangeCallback={onCountryChange}
        ownershipChangeCallback={onMyProjectChange}
        statusChangeCallback={onStatusChange}
        searchTextChangeCallback={onSearchTextChange}
        searchTextInitialValue={_.get(
          props.searchParams,
          PROJECT_SEARCH_QUERY_KEY,
        )}
        countryInitialValue={getObjectValue(
          props.searchParams,
          PROJECT_COUNTRY_QUERY_KEY,
        )}
        ownershipInitialValue={
          _.get(props.searchParams, PROJECT_OWNERSHIP_QUERY_KEY) === 'true'
        }
        statusInitialValue={_.get(props.searchParams, PROJECT_STATUS_QUERY_KEY)}
        moreFiltersInitialValue={getObjectValue(
          props.searchParams,
          PROJECT_MORE_FILTERS_QUERY_KEY,
        )}
        filterFields={[
          FILTER_FIELDS.myCampaign,
          FILTER_FIELDS.country,
          FILTER_FIELDS.projectStatus,
        ]}
      />
      {searchProjectRequest.isPending ? (
        <Flex justify="center" align="center" style={{ height: '50vh' }}>
          <Spin />
        </Flex>
      ) : (
        <React.Fragment>
          <div className="project-listing-view">
            {projects && projects.length > 0 ? (
              <Flex vertical style={{ marginTop: '24px' }}>
                <Row
                  gutter={[16, 16]}
                  style={{ maxWidth: '1200px', margin: '0 auto' }}
                >
                  {projects.map((project) => {
                    return (
                      <ProjectCard
                        key={project.projectID}
                        router={props.router}
                        countries={countries}
                        project={project}
                        height={'160px'}
                        actions={actions}
                      />
                    );
                  })}
                </Row>
                <Flex justify="flex-end" style={{ width: '100%' }}>
                  <Pagination
                    align="center"
                    current={page}
                    pageSize={pageSize}
                    showSizeChanger={false}
                    total={projectSearchHits || 0}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`
                    }
                    onChange={onPageChange}
                    size="small"
                    style={{
                      margin: '24px 20px',
                    }}
                  />
                </Flex>
              </Flex>
            ) : (
              <div className="no-project-found">
                <NoContentPlaceHolder
                  icon={NoProjectsPlaceholderIcon}
                  content={'No projects found'}
                  subContent={
                    'Type in the project description or try modifying your search filters.'
                  }
                />
              </div>
            )}
          </div>
        </React.Fragment>
      )}
      {addCampaignModalMeta.showModal && (
        <AddCampaignForm
          project={addCampaignModalMeta.project}
          user={user}
          countries={countries}
          configs={configs}
          router={props.router}
          createCampaign={(data, projectID) => {
            dispatch(createUCDCampaign(data, projectID));
          }}
          closeModal={() => setAddCampaignModalMeta({ showModal: false })}
        />
      )}
    </div>
  );
};

export default ProjectSearchView;
